import { decodeIfBase64 } from './Analysis/getPossibleBase64Decodings';
import { contentOrBase64DecodedContent } from './contentOrBase64DecodedContent';
import { urlToURL } from './utils/domains';

type Direction = 'sent' | 'received';

export interface ContentSourceBase {
  readonly content: string;
  decodedFromBase64?: true;
  readonly direction: Direction;
}

export interface ContentSourceUrlComponent extends ContentSourceBase {
  readonly location: 'url:host' | 'url:path' | 'url:query' | 'url:query:key';
  readonly positionWithinUrl: number;
  readonly direction: 'sent';
}

export interface ContentSourceUrlQueryValueComponent extends ContentSourceBase {
  readonly location: 'url:query:value';
  readonly positionWithinUrl: number;
  readonly queryKey: string;
  readonly direction: 'sent';
}
export interface ContentSourceReferer extends ContentSourceBase {
  location: 'referer';
  direction: 'sent';
}
export interface ContentSourceBodyFormDataKey extends ContentSourceBase {
  readonly location: 'body:form:key';
  readonly direction: 'sent';
}
export interface ContentSourceBodyFormDataValue extends ContentSourceBase {
  readonly location: 'body:form:value';
  readonly direction: 'sent';
  readonly key: string;
}
export interface ContentSourceBodySentRaw extends ContentSourceBase {
  readonly location: 'body:raw';
  readonly direction: 'sent';
  readonly bodyIndex: number;
}
export interface ContentSourceBodySentJson extends ContentSourceBase {
  readonly location: 'body:json:value' | 'body:json:key';
  readonly key: string;
  readonly direction: 'sent';
}
export interface ContentSourceCookieNameSent extends ContentSourceBase {
  readonly location: 'cookie:name';
  readonly direction: 'sent';
}
export interface ContentSourceCookieValueSent extends ContentSourceBase {
  readonly location: 'cookie:value';
  readonly direction: 'sent';
  readonly cookieName: string;
}
export interface ContentSourceHeaderNameSent extends ContentSourceBase {
  readonly location: 'header:name';
  readonly direction: 'sent';
}
export interface ContentSourceHeaderValueSent extends ContentSourceBase {
  readonly location: 'header:value';
  readonly direction: 'sent';
  readonly headerName: string;
}
export interface ContentSourceHeaderValueReceived extends ContentSourceBase {
  readonly location: 'header:value';
  readonly direction: 'received';
  readonly headerName: string;
}
export interface ContentSourceHeaderNameReceived extends ContentSourceBase {
  readonly location: 'header:name';
  readonly direction: 'received';
}
export interface ContentSourceBodyReceived extends ContentSourceBase {
  readonly location: 'body';
  readonly direction: 'received';
  readonly isBase64Encoded: boolean;
}
export interface ContentSourceWebSocketFrameSent extends ContentSourceBase {
  readonly location: 'frame';
  readonly direction: 'sent';
  readonly isBase64Encoded: boolean;
}
export interface ContentSourceWebSocketFrameReceived extends ContentSourceBase {
  readonly location: 'frame';
  readonly direction: 'received';
  readonly isBase64Encoded: boolean;
}
export type ContentSourceProvidedByHarFormat =
  ContentSourceUrlComponent |
  ContentSourceUrlQueryValueComponent |
  ContentSourceBodyFormDataKey |
  ContentSourceBodyFormDataValue |
  ContentSourceBodySentRaw |
  ContentSourceBodySentJson |
  ContentSourceCookieNameSent |
  ContentSourceCookieValueSent |
  ContentSourceHeaderNameSent |
  ContentSourceHeaderValueSent |
  ContentSourceHeaderValueReceived |
  ContentSourceHeaderNameReceived |
  ContentSourceBodyReceived |
  ContentSourceWebSocketFrameSent |
  ContentSourceWebSocketFrameReceived |
  never;

export type ContentSource = 
  ContentSourceProvidedByHarFormat |
  ContentSourceWebSocketFrameReceived |
  ContentSourceWebSocketFrameSent;
//  ContentSourceWebSocketFrameReceived | 

// HAR format does not support
//  ContentSourceWebSocketFrameSent |
//  ContentSourceWebSocketFrameReceived | 
export type ContentSourceHar = ContentSourceProvidedByHarFormat & {
  entry: HARFormatEntry;
}

export const urlToContentSourceBases = (
  urlObjectOrString?: URL | string, {
    urlHandling = 'query-components'
}: {
  urlHandling?: 'single-query' | 'query-components';
} = {}
) : (ContentSourceUrlComponent | ContentSourceUrlQueryValueComponent)[] => {
  const url = typeof urlObjectOrString === 'string' ? urlToURL(urlObjectOrString) : urlObjectOrString;
  if (url == null) return [];
  const result: (ContentSourceUrlComponent | ContentSourceUrlQueryValueComponent)[] = [];
  let positionWithinUrl = url.protocol.length + 2;
  result.push({ positionWithinUrl, content: url.host, location: 'url:host', direction: 'sent' });
  positionWithinUrl += url.origin.length;
  result.push({ positionWithinUrl, content: url.pathname, location: 'url:path', direction: 'sent' });
  // The hash is never sent to the server, so it's not a type of sent/received content
  // positionWithinUrl = url.href.length - url.hash.length
  // result.push({ positionWithinUrl, content: url.hash, location: 'url:hash', direction: 'sent' });
  if (urlHandling === 'single-query') {
    positionWithinUrl = url.href.length - (url.hash.length + url.search.length);
    result.push({ positionWithinUrl, content: url.search, location: 'url:query', direction: 'sent' });
  } else {
    const positionOfSearch = url.href.length - (url.hash.length + url.search.length);
    for (const [key, value] of url.searchParams) {
      positionWithinUrl = positionOfSearch + url.search.indexOf(`${key}=`);
      result.push({ positionWithinUrl, ...contentOrBase64DecodedContent(key), location: 'url:query:key', direction: 'sent' });
      positionWithinUrl += key.length + 1;
      result.push({ positionWithinUrl, ...contentOrBase64DecodedContent(value), location: 'url:query:value', direction: 'sent', queryKey: key });
    }
  }
  return result.filter( c => c.content.length > 0);
}

export type JSON = string | number | boolean | {[key: string]: JSON} | JSON[] | null;

export const bodyStringToContentSources = (bodyString?: string) =>	{
	const toJsonContentSourcesRecursive = (recursiveKey: string, value: JSON): ContentSourceBodySentJson[] => {
		if (value == null) return [];
		if (Array.isArray(value)) {
			return ([] as ContentSourceBodySentJson[]).concat(
				...value.map( (v, i) => toJsonContentSourcesRecursive(`${recursiveKey}[${i}]`, v) )
			);
		} else if (typeof value === "object") {
			return ([] as ContentSourceBodySentJson[]).concat(
				...Object.entries(value).map( ([key, v]) => ([
					...(recursiveKey.length < 3 ? [] : [{ direction: 'sent', location: 'body:json:key', key: recursiveKey, content: `${recursiveKey}.${key}` } satisfies ContentSourceBodySentJson]),
					...toJsonContentSourcesRecursive(`${recursiveKey}${recursiveKey.length === 0 ? '' : '.'}${key}`, v)
				]))
			)
		} else if (typeof value === "string" && value.length > 2) {
			return [{ direction: 'sent', location: 'body:json:value', key: recursiveKey, ...contentOrBase64DecodedContent(value) } satisfies ContentSourceBodySentJson];
		} else if (typeof value === "number" && !Number.isNaN(value) && Number.isFinite(value)) {
			return [{ direction: 'sent', location: 'body:json:value', key: recursiveKey, content: `${value}` } satisfies ContentSourceBodySentJson];
    }

		// We don't return numbers, boolean, or short strings as there's nothing worth searching
		return [];
	}
  if (bodyString == null) return [];
  const text = decodeIfBase64(bodyString);
  try {
    return toJsonContentSourcesRecursive("", JSON.parse(text) as JSON);
  } catch {
    return [{ direction: 'sent', location: 'body:raw', ...contentOrBase64DecodedContent(text), bodyIndex: -1 } satisfies ContentSourceBodySentRaw as ContentSourceBodySentRaw];
  }
}

export type ContentLocation = ContentSource['location'];

export const enhancedContentSourceLocation = (cs: Omit<ContentSource, 'content'>) => {
  const contentSource = cs as ContentSource;
  const received = contentSource.direction === 'received' ? 'received:' : '';
  const decodingOf: <T extends string>(s: T) => T | `base64Decoded(${T})` = cs.decodedFromBase64 ? ((s) => `base64Decoded(${s})`) : ((s) => s);
	switch (contentSource.location) {
		case "cookie:value":
			return decodingOf(`${received}${contentSource.location}:${contentSource.cookieName}` as const);
		case "header:value":
			return decodingOf(`${received}${contentSource.location}:${contentSource.headerName}` as const);
		case "url:query:value":
			return decodingOf(`${received}${contentSource.location}:${contentSource.queryKey}` as const);
    case "body:form:value":
      return decodingOf(`${contentSource.location}:${contentSource.key}` as const);
    case "body:json:key":
    case "body:json:value":
      return decodingOf(`${contentSource.location}:${contentSource.key}` as const);
	}
	return decodingOf(`${received}${contentSource.location}` as const);
}
export type EnhancedContentSourceLocation = ReturnType<typeof enhancedContentSourceLocation>;
